import { defineStore } from 'pinia';
import { ref } from 'vue';
import { isNaNAndInfinity } from '@/utils/storageTracking/IsNaNAndInfinity.ts';
import { DataApi } from '@/api/storageTracking/DataApi.ts';
import { TRolesForSearch } from '@/interfaces/storageTracking/ILatestData.ts';
import { IRedactedProjectData } from '@/interfaces/storageTracking/IRedactedProjectData.ts';
import { IProjectSearchResponse } from '@/interfaces/storageTracking/IProjectSearchResponse.ts';
import { useFilterDataStore } from './FilterDataStore.ts';

export const useAllDataStore = defineStore('allDataStore', () => {
    const filterDataStore = useFilterDataStore();
    const allRedactedDataUnfiltered = ref<IProjectSearchResponse>({ filteredProjects: [], numberOfHiddenProjects: 0 });

    const trialUserCompanyLimits = ref<Record<string, boolean>>();

    let years: Array<number> = [];
    const animateYears = ref<boolean>(false);

    function setYears (): void {
        const yearsList: Array<number> = [];
        for (let t = filterDataStore.startDate; t <= filterDataStore.endDate; t += 1) {
            yearsList.push(t);
        }
        years = yearsList;
    }

    function updateAnimateYears (bool: boolean): void {
        animateYears.value = bool;
    }

    const roles: TRolesForSearch[] = [
        'developersSearch',
        'financersSearch',
        'operatorsForSearch',
        'ownersForSearch',
        'tradersForSearch',
        'technologyProvidersForSearch',
    ];

    const availableKeyValueRoles = {
        developersSearch: 'Developer',
        ownersForSearch: 'Owner',
        operatorsForSearch: 'Operator',
        financersSearch: 'Financer',
        tradersForSearch: 'Trader',
        technologyProvidersForSearch: 'Technology Provider',
    };

    function parseDate (dateString: string): Date {
        const [day, month, year] = dateString.split('/').map(Number);
        return new Date(year, month - 1, day);
    }

    const fetchingData = ref<boolean>(false);

    function filterCheckStringIncludes (data: string, filterValue: string): boolean {
        return filterValue.length === 0 || data.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase());
    }

    async function getAllRedactedDataUnfiltered (): Promise<void> {
        allRedactedDataUnfiltered.value = await DataApi.getAllDataFilteredRedacted(filterDataStore.neutralFilters);
    }

    function setMaxes (): void {
        const dataWithOrWithoutPHS: Array<IRedactedProjectData> = filterDataStore.usePumpedStorage ? allRedactedDataUnfiltered.value.filteredProjects : allRedactedDataUnfiltered.value.filteredProjects.filter(item => !item.technology.some(tech => tech.toLowerCase().includes('pumped')));
        filterDataStore.maxDurationCapacity = Math.max(
            ...dataWithOrWithoutPHS.map((i: IRedactedProjectData) => {
                const duration = i.storageCapacityMwH ? i.storageCapacityMwH / i.powerCapacityMw : 0;
                return Math.round(isNaNAndInfinity(duration));
            }),
            0,
        );

        filterDataStore.maxStorageCapacity = Math.max(
            ...dataWithOrWithoutPHS.map((i: IRedactedProjectData) => (i.storageCapacityMwH ? i.storageCapacityMwH : 0)),
            0,
        );

        filterDataStore.maxPowerCapacity = Math.max(
            ...dataWithOrWithoutPHS.map((i: IRedactedProjectData) => i.powerCapacityMw),
            0,
        );

        filterDataStore.updateStorageCapacityRange([0, filterDataStore.maxStorageCapacity]);
        filterDataStore.updatePowerCapacityRange([0, filterDataStore.maxPowerCapacity]);
        filterDataStore.updateDurationRange([0, filterDataStore.maxDurationCapacity]);
    }

    async function setTrialLimits (): Promise<void> {
        const trialLimits = await DataApi.getTrialUserLimits();
        trialUserCompanyLimits.value = trialLimits.companies;
    }

    function updateUsePumpedStorage (bool: boolean): void {
        filterDataStore.usePumpedStorage = bool;
        setMaxes();
    }

    async function initialise (): Promise<void> {
        setYears();
        setTrialLimits();
        await getAllRedactedDataUnfiltered();
        setMaxes();
    }

    initialise();

    return {
        allRedactedDataUnfiltered,
        animateYears,
        fetchingData,
        filterCheckStringIncludes,
        getAllRedactedDataUnfiltered,
        roles,
        parseDate,
        setMaxes,
        setTrialLimits,
        updateAnimateYears,
        years,
        availableKeyValueRoles,
        trialUserCompanyLimits,
        updateUsePumpedStorage,
    };
});
